import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ViewMode = 'grid' | 'gantt' | 'split' | 'card' | 'calendar';

interface SplitConfig {
  leftWidth: number; // percentage (0-100)
  leftCollapsed: boolean;
  rightCollapsed: boolean;
}

interface ViewState {
  // Current view mode per sheet
  viewModes: Record<string, ViewMode>;

  // Split view configuration per sheet
  splitConfigs: Record<string, SplitConfig>;

  // Synchronized scroll position (for split view)
  scrollTop: number;
  scrollLeft: number;

  // Row highlighting
  hoveredTaskId: string | null;
  selectedTaskIds: string[];

  // Actions
  getViewMode: (sheetId: string) => ViewMode;
  setViewMode: (sheetId: string, mode: ViewMode) => void;

  getSplitConfig: (sheetId: string) => SplitConfig;
  setSplitWidth: (sheetId: string, width: number) => void;
  toggleLeftCollapsed: (sheetId: string) => void;
  toggleRightCollapsed: (sheetId: string) => void;

  setScrollPosition: (top: number, left?: number) => void;
  setHoveredTaskId: (taskId: string | null) => void;
  setSelectedTaskIds: (taskIds: string[]) => void;
  toggleTaskSelection: (taskId: string) => void;

  reset: () => void;
}

const DEFAULT_SPLIT_CONFIG: SplitConfig = {
  leftWidth: 50,
  leftCollapsed: false,
  rightCollapsed: false,
};

const initialState = {
  viewModes: {} as Record<string, ViewMode>,
  splitConfigs: {} as Record<string, SplitConfig>,
  scrollTop: 0,
  scrollLeft: 0,
  hoveredTaskId: null as string | null,
  selectedTaskIds: [] as string[],
};

export const useViewStore = create<ViewState>()(
  persist(
    (set, get) => ({
      ...initialState,

      getViewMode: (sheetId: string) => {
        return get().viewModes[sheetId] || 'grid';
      },

      setViewMode: (sheetId: string, mode: ViewMode) => {
        set((state) => ({
          viewModes: {
            ...state.viewModes,
            [sheetId]: mode,
          },
        }));
      },

      getSplitConfig: (sheetId: string) => {
        return get().splitConfigs[sheetId] || DEFAULT_SPLIT_CONFIG;
      },

      setSplitWidth: (sheetId: string, width: number) => {
        set((state) => ({
          splitConfigs: {
            ...state.splitConfigs,
            [sheetId]: {
              ...(state.splitConfigs[sheetId] || DEFAULT_SPLIT_CONFIG),
              leftWidth: Math.max(20, Math.min(80, width)),
            },
          },
        }));
      },

      toggleLeftCollapsed: (sheetId: string) => {
        set((state) => {
          const current = state.splitConfigs[sheetId] || DEFAULT_SPLIT_CONFIG;
          return {
            splitConfigs: {
              ...state.splitConfigs,
              [sheetId]: {
                ...current,
                leftCollapsed: !current.leftCollapsed,
                // If collapsing left, expand right
                rightCollapsed: !current.leftCollapsed ? false : current.rightCollapsed,
              },
            },
          };
        });
      },

      toggleRightCollapsed: (sheetId: string) => {
        set((state) => {
          const current = state.splitConfigs[sheetId] || DEFAULT_SPLIT_CONFIG;
          return {
            splitConfigs: {
              ...state.splitConfigs,
              [sheetId]: {
                ...current,
                rightCollapsed: !current.rightCollapsed,
                // If collapsing right, expand left
                leftCollapsed: !current.rightCollapsed ? false : current.leftCollapsed,
              },
            },
          };
        });
      },

      setScrollPosition: (top: number, left?: number) => {
        set({
          scrollTop: top,
          ...(left !== undefined && { scrollLeft: left }),
        });
      },

      setHoveredTaskId: (taskId: string | null) => {
        set({ hoveredTaskId: taskId });
      },

      setSelectedTaskIds: (taskIds: string[]) => {
        set({ selectedTaskIds: taskIds });
      },

      toggleTaskSelection: (taskId: string) => {
        set((state) => {
          const isSelected = state.selectedTaskIds.includes(taskId);
          return {
            selectedTaskIds: isSelected
              ? state.selectedTaskIds.filter((id) => id !== taskId)
              : [...state.selectedTaskIds, taskId],
          };
        });
      },

      reset: () => set(initialState),
    }),
    {
      name: 'view-settings',
      partialize: (state) => ({
        viewModes: state.viewModes,
        splitConfigs: state.splitConfigs,
      }),
    }
  )
);

// Hook for URL-synced view mode
export function useUrlViewMode(sheetId: string) {
  const { getViewMode, setViewMode } = useViewStore();

  // Get view from URL if present
  const getViewFromUrl = (): ViewMode | null => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const view = params.get('view');
    if (view && ['grid', 'gantt', 'split', 'card', 'calendar'].includes(view)) {
      return view as ViewMode;
    }
    return null;
  };

  // Update URL with view mode
  const updateUrl = (mode: ViewMode) => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    url.searchParams.set('view', mode);
    window.history.replaceState({}, '', url.toString());
  };

  // Get current view mode (URL takes precedence)
  const currentMode = getViewFromUrl() || getViewMode(sheetId);

  // Set view mode and update URL
  const setMode = (mode: ViewMode) => {
    setViewMode(sheetId, mode);
    updateUrl(mode);
  };

  return { currentMode, setMode };
}
